import closeIcon from "src/assests/images/dashboard/Vector 3.png";
import defaultImg from "src/assests/images/dashboard/default-item-image.jpg";
import editIcon from "src/assests/images/dashboard/pencil.png";
import { useCallback, useEffect, useMemo, useState } from "react";
import Dropzone from "react-dropzone";
import * as Yup from "yup";
import { useFormik } from "formik";
import { GRAPHQL_ENDPOINT, S3_BASE_URL } from "src/constants/endpoints";
import {
  ADD_CATEGORY,
  UPDATE_CATEGORY,
  UPLOAD_CATEGORY_IMAGE,
} from "src/constants/mutations";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { useAuth } from "src/contexts/auth-provider";
import axios from "axios";
import { successToast } from "src/utils/toasts";
import { mimeTypeToExtension } from "src/components/RestaurantSetting/RestaurantSettingComponent";
import { GET_MENU_CATEGORIES } from "src/constants/queries";
import { useDispatch } from "react-redux";
import {
  set_menu_categories,
  set_menu_category_loading,
  bump_categories_revision,
} from "src/store/features/category-slice";
import Spinner from "src/components/common/Spinner";
import { PortaledModalFrame } from "src/components/common/PortaledModalFrame";
import MenuItemLocaleFields from "src/components/common/MenuItemLocaleFields";
import { useStorePosLocales } from "src/hooks/useStorePosLocales";
import {
  buildLocaleTranslationMap,
  buildTranslationsPayload,
  LocaleTranslationMap,
  syncLegacyNameFieldsFromLocaleTranslations,
} from "src/utils/localeTranslations";

const MenuCategoryModal = ({
  setShowCreateModal,
  menuCategoryTitle,
  editMenuCategory,
  onCategoriesSaved,
}: {
  setShowCreateModal: () => void;
  menuCategoryTitle: string;
  editMenuCategory: any;
  onCategoriesSaved?: () => void;
}) => {
  const { user } = useAuth();
  const { axBe } = useAxiosInterceptor();
  const { hasRestaurant } = useAuth();
  const [menuCategoryImage, setMenuCategoryImage] = useState<any>(null);
  const dispatch = useDispatch();
  const [isCategoryImageLoading, setIsCategoryImageLoading] = useState(false);
  const { enabledLocaleOptions, enabledLocales, defaultLocale } = useStorePosLocales();

  const initialLocaleTranslations = useMemo(
    () =>
      buildLocaleTranslationMap(editMenuCategory?.translations, enabledLocales, {
        englishName: editMenuCategory?.name,
        nonEnglishName: editMenuCategory?.nonEnglishName,
      }),
    [editMenuCategory, enabledLocales]
  );
  const [localeTranslations, setLocaleTranslations] = useState<LocaleTranslationMap>(initialLocaleTranslations);

  useEffect(() => {
    setLocaleTranslations(initialLocaleTranslations);
  }, [initialLocaleTranslations]);

  const formik = useFormik({
    initialValues: {
      name: "",
      nonEnglishName: "",
      isActive: true,
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is Required"),
    }),
    onSubmit: () => {},
  });
  const { setValues } = formik;

  const handleLocaleTranslationsChange = useCallback(
    (next: LocaleTranslationMap) => {
      const legacy = syncLegacyNameFieldsFromLocaleTranslations(next);
      setLocaleTranslations(next);
      formik.setFieldValue("name", legacy.name, true);
      formik.setFieldValue("nonEnglishName", legacy.nonEnglishName, true);
    },
    [formik]
  );

  const handleAddMenuCategory = async () => {
    try {
      const legacy = syncLegacyNameFieldsFromLocaleTranslations(localeTranslations);
      const translations = buildTranslationsPayload(localeTranslations, enabledLocales);
      let variables: any = {};
      if (!!editMenuCategory) {
        variables = {
          updateCategoryInput: {
            name: legacy.name || formik.values.name,
            nonEnglishName: legacy.nonEnglishName || undefined,
            categoryCorrelationId: editMenuCategory?.correlationId,
            isActive: formik.values.isActive,
            translations,
          },
        };
      } else {
        variables = {
          addCategoryInput: {
            name: legacy.name || formik.values.name,
            nonEnglishName: legacy.nonEnglishName || undefined,
            isActive: formik.values.isActive,
            storeCorrelationId: hasRestaurant.correlationId,
            translations,
          },
        };
      }
      const { data : updatedOrAddedMenuCategory } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: !!editMenuCategory ? UPDATE_CATEGORY : ADD_CATEGORY,
        variables: variables,
      });
      if (updatedOrAddedMenuCategory?.errors?.length) {
        throw updatedOrAddedMenuCategory?.errors;
      }
      if (
        (updatedOrAddedMenuCategory?.data?.addCategory?.correlationId && menuCategoryImage?.file) ||
        (editMenuCategory?.correlationId && menuCategoryImage?.file)
      ) {
        await uploadCategoryImage(
          updatedOrAddedMenuCategory?.data?.addCategory?.correlationId ||
            editMenuCategory?.correlationId
        );
      } else if (editMenuCategory?.correlationId && !menuCategoryImage?.file) {
        getMenuCategories();
        successToast("Category Edited!");
        setShowCreateModal();
      } else if (updatedOrAddedMenuCategory?.data?.addCategory?.correlationId && !menuCategoryImage?.file) {
        getMenuCategories();
        successToast("Category Created!");
        setShowCreateModal();
      }
    } catch (error) {
      console.error("Error creating user:", error);
    }
  };

  const uploadCategoryImage = async (categoryCorrelationId: string) => {
    setIsCategoryImageLoading(true);
    const legacy = syncLegacyNameFieldsFromLocaleTranslations(localeTranslations);
    const variables = {
      createCategoryPreSignedUrlInput: {
        storeUserCorrelationId: user?.storeUser?.correlationId,
        categoryName: legacy.name || formik.values.name,
        fileNameExtension: mimeTypeToExtension[menuCategoryImage?.file?.type],
        storeCorrelationId: hasRestaurant?.correlationId,
        categoryCorrelationId,
      },
    };
    try {
      const res = await axBe.post(GRAPHQL_ENDPOINT, {
        query: UPLOAD_CATEGORY_IMAGE,
        variables: variables,
      });
      if (!res?.data?.errors?.length) {
        const awsUrl =
          res.data?.data?.createUploadPreSignedUrlForCategory?.preSignedUrl;
        await axios.put(awsUrl, menuCategoryImage.file, {
          headers: {
            "Content-Type": menuCategoryImage?.file?.type,
            "x-amz-acl": "public-read",
          },
        });
        getMenuCategories();
        setIsCategoryImageLoading(false);
        setShowCreateModal();
        successToast(
          !!editMenuCategory ? "Category Edited!" : "Category Added!"
        );
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      setIsCategoryImageLoading(false);
    }
  };

  const getMenuCategories = async () => {
    dispatch(set_menu_category_loading(true))
    try {
      const { data : menuCategories } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: GET_MENU_CATEGORIES,
        variables: {
          storeCorrelationId: hasRestaurant.correlationId,
          activeFilter: "ACTIVE",
        },
      });
      dispatch(
        set_menu_categories({
          categories: menuCategories?.data?.categoriesByStoreCorrelationId || [],
        })
      );
      dispatch(bump_categories_revision());
      onCategoriesSaved?.();
      dispatch(set_menu_category_loading(false))
    } catch (error) {
      console.error(error);
      dispatch(set_menu_category_loading(false))
    }
  };

  const handleCategoryImageChange = (menuCategoryImage: any) => {
    setMenuCategoryImage({
      url: URL.createObjectURL(menuCategoryImage),
      file : menuCategoryImage,
    });
  };

  useEffect(() => {
    if (editMenuCategory) {
      setValues({
        name: editMenuCategory.name ?? "",
        nonEnglishName: editMenuCategory.nonEnglishName ?? "",
        isActive: editMenuCategory.active !== false,
      });
      if (editMenuCategory?.bucketName) {
        setMenuCategoryImage({
          url: S3_BASE_URL + editMenuCategory?.bucketName,
          file: null,
        });
      }
    } else {
      setValues({ name: "", nonEnglishName: "", isActive: true });
      setMenuCategoryImage(null);
    }
  }, [editMenuCategory, setValues]);

  const isDisabled = !formik.isValid || !formik.dirty;
  const defaultLocaleName = localeTranslations[defaultLocale]?.name?.trim() ?? "";
  const categoryNameError = !defaultLocaleName ? "Name is Required" : undefined;

  return (
    <PortaledModalFrame onClose={setShowCreateModal} panelStyle={{ maxWidth: 788 }}>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-[#F1F1F2] px-5 py-4 sm:px-8">
          <span id="modal-title" className="text-xl font-semibold text-[#252525]">
            {menuCategoryTitle}
          </span>
          <button
            type="button"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100"
            onClick={setShowCreateModal}
            aria-label="Close"
          >
            <img className="h-3 w-3" src={closeIcon} alt="" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-5 sm:px-8">
                {/* Image with category */}
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                  <Dropzone
                    accept={{
                      "image/png": [".png"],
                      "image/jpg": [".jpg"],
                      "image/jpeg": [".jpeg"],
                    }}
                    onDrop={(acceptedFiles) =>
                      handleCategoryImageChange(acceptedFiles[0])
                    }
                  >
                    {({ getRootProps, getInputProps }) => (
                      <div>
                        <div className="w-[67px] h-[67px] rounded-lg border border-[#F1F1F2] flex justify-center items-center relative">
                          <img
                            src={
                              menuCategoryImage?.url ? menuCategoryImage?.url :
                              editMenuCategory?.bucketKeyName
                                ? editMenuCategory?.bucketKeyName.includes(
                                    "blob"
                                  )
                                  ? editMenuCategory?.bucketKeyName
                                  : S3_BASE_URL +
                                    editMenuCategory?.bucketKeyName
                                : defaultImg
                            }
                            alt=""
                            className="w-[55px] h-[55px] object-cover"
                          />
                        </div>
                        <button>
                          <img
                            src={editIcon}
                            alt=""
                            className="w-6 h-6 absolute top-[85px] left-[83px]"
                            {...getRootProps()}
                          />
                          <input {...getInputProps()} />
                        </button>
                      </div>
                    )}
                  </Dropzone>
                  <div className="min-w-0 flex-1 sm:ml-4">
                    <MenuItemLocaleFields
                      enabledLocaleOptions={enabledLocaleOptions}
                      defaultLocale={defaultLocale}
                      localeTranslations={localeTranslations}
                      onChange={handleLocaleTranslationsChange}
                      nameError={categoryNameError}
                      entityLabel="Category"
                      showDescription={false}
                    />
                  </div>
                </div>
                <div className="divider border-[#EBF5FF] border-[1px] mt-2 mb-6"></div>
                {!!editMenuCategory ? (
                  <div className="mt-4 mb-2">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={formik.values.isActive !== false}
                        onChange={(e) => formik.setFieldValue("isActive", e.target.checked)}
                        className="receipt-lang-check w-6 h-6 min-w-[24px] min-h-[24px] shrink-0 rounded border border-[#9B9B9B] cursor-pointer appearance-none"
                      />
                      <span className="text-sm">Category is active (visible on customer menus)</span>
                    </label>
                  </div>
                ) : null}
        </div>
        <div className="flex shrink-0 justify-end border-t border-[#F1F1F2] px-5 py-4 sm:px-8">
          {isCategoryImageLoading ? (
            <Spinner />
          ) : (
            <button
              type="button"
              className={`flex h-9 items-center justify-center gap-2 rounded-md bg-orange px-8 text-xs text-white hover:bg-orange/90 ${
                isDisabled ? "opacity-20" : ""
              }`}
              onClick={() => handleAddMenuCategory()}
              disabled={isDisabled || !defaultLocaleName}
            >
              Confirm
            </button>
          )}
        </div>
      </div>
    </PortaledModalFrame>
  );
};

export default MenuCategoryModal
